/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { createClassNames } from '@mpb-com/feather/helpers/styles';
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import { ContentCollectionVariant } from '@toucan/client/core/contentful';

import { Link } from '@toucan/client/shared/components/link';

import { styles } from './content-collection.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface ContentCollectionItem {
    image: ReactNode;
    title: ReactNode;
    content: ReactNode;
    link?: string | null;
    linkTitle?: string | null;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ContentCollectionProps {
    /**
     * Variant of the content collection
     */
    variant: ContentCollectionVariant;

    /**
     * Heading to show in the component
     */
    heading: ReactNode;

    /**
     * Items to show
     */
    items: ContentCollectionItem[];

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

const StyledContentCollection = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const ContentCollection: FunctionComponent<ContentCollectionProps> = ({
    heading,
    items,
    ...otherProps
}: ContentCollectionProps) => {
    const { variant } = otherProps;

    const headingSize =
        variant === ContentCollectionVariant.TWO_COL_CARDS
            ? HeadingSize.H3
            : HeadingSize.H4;

    const isThreeCircularImageVariant =
        variant === ContentCollectionVariant.THREE_COL_CIRCULAR_IMAGES;

    const isTwoColumnCardsVariant =
        variant === ContentCollectionVariant.TWO_COL_CARDS;

    const isTwoColumnImagesVariant =
        variant === ContentCollectionVariant.TWO_COL_IMAGES_LEFT;

    const classNames = createClassNames(
        'content-collection',
        isThreeCircularImageVariant &&
            'content-collection--three-circular-images',
        isTwoColumnCardsVariant && 'content-collection--two-column-cards',
        isTwoColumnImagesVariant && 'content-collection--two-column-images'
    );

    return (
        <StyledContentCollection
            className={classNames}
            heading={heading}
            items={items}
            {...otherProps}
        >
            <Heading
                size={HeadingSize.H2}
                className="content-collection-heading"
            >
                {heading}
            </Heading>
            <ul className="content-collection-items">
                {items.map(({ image, content, title, link, linkTitle }, i) => {
                    const callToAction = link && linkTitle && (
                        <div className="content-collection-item-action">
                            <Link href={link} hasArrow>
                                {linkTitle}
                            </Link>
                        </div>
                    );
                    return (
                        <li key={i} className="content-collection-item">
                            <div className="content-collection-item-image">
                                {image}
                            </div>
                            <div className="content-collection-item-content">
                                <Heading size={headingSize}>{title}</Heading>
                                {content}
                                {callToAction}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </StyledContentCollection>
    );
};
